import React, { ChangeEvent, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Close from "@mui/icons-material/Close";
import { useAppContext } from "../../../../context/AppContext";
import { DISABLE_RTL_STYLES, DISABLE_RTL_PROPS } from "../../../../utils/rtlUtils";
import { COMMON_MUI_STYLES } from "../../../../styles";

interface NewPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  newPassword: string;
  confirmNewPassword: string;
  onPasswordChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showNewPassword: boolean;
  showConfirmNewPassword: boolean;
  onToggleNewPassword: () => void;
  onToggleConfirmNewPassword: () => void;
  isLoading: boolean;
}

export const NewPasswordModal: React.FC<NewPasswordModalProps> = ({
  open,
  onClose,
  onSubmit,
  newPassword,
  confirmNewPassword,
  onPasswordChange,
  showNewPassword,
  showConfirmNewPassword,
  onToggleNewPassword,
  onToggleConfirmNewPassword,
  isLoading,
}) => {
  const { getTranslation } = useAppContext();
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Handle auto-fill: ensure type is controlled even after browser auto-fill
  // iOS Safari needs more aggressive handling
  useEffect(() => {
    const ensureType = (input: HTMLInputElement | null, showPassword: boolean) => {
      if (input) {
        const expectedType = showPassword ? "text" : "password";
        if (input.type !== expectedType) {
          // For iOS, we need to be more aggressive
          if (isIOS) {
            // Store current value and selection
            const currentValue = input.value;
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;
            
            // Force type change
            input.type = expectedType;
            
            // Restore value and selection (iOS sometimes clears it)
            if (input.value !== currentValue) {
              input.value = currentValue;
            }
            
            // Restore selection if it was set
            if (selectionStart !== null && selectionEnd !== null) {
              setTimeout(() => {
                input.setSelectionRange(selectionStart, selectionEnd);
              }, 0);
            }
          } else {
            input.type = expectedType;
          }
        }
      }
    };

    const newPasswordInput = newPasswordRef.current?.querySelector('input');
    const confirmPasswordInput = confirmPasswordRef.current?.querySelector('input');

    ensureType(newPasswordInput || null, showNewPassword);
    ensureType(confirmPasswordInput || null, showConfirmNewPassword);

    const checkTypes = () => {
      ensureType(newPasswordInput || null, showNewPassword);
      ensureType(confirmPasswordInput || null, showConfirmNewPassword);
    };

    // Multiple timeouts for iOS (auto-fill can be delayed)
    const timeoutId1 = setTimeout(checkTypes, 100);
    const timeoutId2 = setTimeout(checkTypes, 300);
    const timeoutId3 = setTimeout(checkTypes, 500);
    
    if (newPasswordInput) {
      newPasswordInput.addEventListener('input', checkTypes);
      newPasswordInput.addEventListener('change', checkTypes);
      newPasswordInput.addEventListener('blur', checkTypes);
    }
    if (confirmPasswordInput) {
      confirmPasswordInput.addEventListener('input', checkTypes);
      confirmPasswordInput.addEventListener('change', checkTypes);
      confirmPasswordInput.addEventListener('blur', checkTypes);
    }

    // For iOS, also use MutationObserver
    let observer1: MutationObserver | null = null;
    let observer2: MutationObserver | null = null;
    if (isIOS) {
      if (newPasswordInput) {
        observer1 = new MutationObserver(checkTypes);
        observer1.observe(newPasswordInput, {
          attributes: true,
          attributeFilter: ['type']
        });
      }
      if (confirmPasswordInput) {
        observer2 = new MutationObserver(checkTypes);
        observer2.observe(confirmPasswordInput, {
          attributes: true,
          attributeFilter: ['type']
        });
      }
    }

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      newPasswordInput?.removeEventListener('input', checkTypes);
      newPasswordInput?.removeEventListener('change', checkTypes);
      newPasswordInput?.removeEventListener('blur', checkTypes);
      confirmPasswordInput?.removeEventListener('input', checkTypes);
      confirmPasswordInput?.removeEventListener('change', checkTypes);
      confirmPasswordInput?.removeEventListener('blur', checkTypes);
      observer1?.disconnect();
      observer2?.disconnect();
    };
  }, [showNewPassword, showConfirmNewPassword, isIOS]);

  return (
    <Dialog
      open={open}
      BackdropProps={{
        style: { backgroundColor: "rgba(255,255,255,0.0)" },
      }}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: 700,
          fontSize: "1.5rem",
          color: "var(--color-text-primary)",
          pt: 3,
          pb: 1,
          position: "relative",
        }}
      >
        {getTranslation("Txt829")}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--color-text-secondary)",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 3,
            textAlign: "center",
            color: "var(--color-text-primary)",
            fontSize: { xs: "0.9rem", sm: "1rem" },
            lineHeight: 1.5,
          }}
        >
          {getTranslation("Txt830")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <TextField
            ref={newPasswordRef}
            {...DISABLE_RTL_PROPS}
            margin="normal"
            fullWidth
            id="newPassword"
            placeholder={getTranslation("Txt831")}
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            autoComplete="new-password"
            value={newPassword}
            onChange={onPasswordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" {...DISABLE_RTL_PROPS}>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleNewPassword();
                      
                      // For iOS, force type change immediately on toggle click (handles iPhone password generator)
                      if (isIOS) {
                        setTimeout(() => {
                          const input = newPasswordRef.current?.querySelector('input');
                          if (input) {
                            const expectedType = !showNewPassword ? "text" : "password";
                            const currentValue = input.value;
                            input.type = expectedType;
                            if (input.value !== currentValue) {
                              input.value = currentValue;
                            }
                          }
                        }, 0);
                      }
                    }}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...COMMON_MUI_STYLES.textField,
              ...DISABLE_RTL_STYLES,
            }}
          />
          <TextField
            ref={confirmPasswordRef}
            {...DISABLE_RTL_PROPS}
            margin="normal"
            fullWidth
            id="confirmNewPassword"
            placeholder={getTranslation("Txt832")}
            name="confirmNewPassword"
            type={showConfirmNewPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirmNewPassword}
            onChange={onPasswordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" {...DISABLE_RTL_PROPS}>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleConfirmNewPassword();
                      
                      // For iOS, force type change immediately on toggle click (handles iPhone password generator)
                      if (isIOS) {
                        setTimeout(() => {
                          const input = confirmPasswordRef.current?.querySelector('input');
                          if (input) {
                            const expectedType = !showConfirmNewPassword ? "text" : "password";
                            const currentValue = input.value;
                            input.type = expectedType;
                            if (input.value !== currentValue) {
                              input.value = currentValue;
                            }
                          }
                        }, 0);
                      }
                    }}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...COMMON_MUI_STYLES.textField,
              ...DISABLE_RTL_STYLES,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          gap: 2,
          px: 4,
          pb: 4,
          pt: 0,
        }}
      >
        <Button
          onClick={onSubmit}
          variant="contained"
          fullWidth
          disabled={isLoading}
          sx={COMMON_MUI_STYLES.button}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: "var(--color-text-inverse)" }} />
            </Box>
          ) : (
            getTranslation("Txt833")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
